import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchProvidersHealth,
  fetchProviderLatencyHistory,
  fetchProviderErrorBreakdown,
  fetchProviderRecentCalls,
  type ProviderHealthRow,
} from '../../api/admin.js';
import { Sparkline } from '../../components/charts/Sparkline.js';
import {
  formatNumber,
  formatPercent,
  formatDurationMs,
  nanoToUsd,
  nanoToUsdCompact,
  formatDateTime,
  shortId,
} from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';

export function ProvidersPage(): ReactElement {
  const [drawer, setDrawer] = useState<ProviderHealthRow | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'providers', 'health'],
    queryFn: fetchProvidersHealth,
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Providers · Health</h1>
          <p className="page-sub">Matriz (provider, model) × calls / errors / p50p95 / costo · 24h</p>
        </div>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <section className="card">
          <table className="data-table" data-testid="providers-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Model</th>
                <th>Kind</th>
                <th>Calls</th>
                <th>Errors</th>
                <th>Error rate</th>
                <th>p50</th>
                <th>p95</th>
                <th>Cost</th>
                <th>Top error</th>
              </tr>
            </thead>
            <tbody>
              {query.data.rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty">Sin actividad en las últimas 24h</td>
                </tr>
              ) : (
                query.data.rows.map((r) => (
                  <ProviderRow
                    key={`${r.provider}-${r.model}-${r.kind}`}
                    row={r}
                    onOpen={() => setDrawer(r)}
                  />
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
      {drawer && drawer.model ? (
        <ProviderDrawer row={drawer} onClose={() => setDrawer(null)} />
      ) : null}
    </>
  );
}

function ProviderRow({
  row,
  onOpen,
}: {
  row: ProviderHealthRow;
  onOpen: () => void;
}): ReactElement {
  return (
    <tr
      onClick={onOpen}
      style={{ cursor: row.model ? 'pointer' : 'default' }}
      data-testid={`provider-row-${row.provider}-${row.model}-${row.kind}`}
    >
      <td>{row.provider}</td>
      <td>{row.model ?? '—'}</td>
      <td>
        <span className={`kind-pill kind-${row.kind}`}>{row.kind}</span>
      </td>
      <td>{formatNumber(row.calls)}</td>
      <td>{formatNumber(row.errorCount)}</td>
      <td className={row.errorRate > 0.05 ? 'bad' : ''}>{formatPercent(row.errorRate)}</td>
      <td>{formatDurationMs(row.p50Ms)}</td>
      <td>{formatDurationMs(row.p95Ms)}</td>
      <td>{nanoToUsdCompact(row.totalCostNano)}</td>
      <td>{row.topErrorKind ?? '—'}</td>
    </tr>
  );
}

function ProviderDrawer({
  row,
  onClose,
}: {
  row: ProviderHealthRow;
  onClose: () => void;
}): ReactElement {
  const provider = row.provider;
  const model = row.model ?? '';

  const latency = useQuery({
    queryKey: ['admin', 'providers', provider, model, 'latency-history'],
    queryFn: () => fetchProviderLatencyHistory(provider, model, '24h'),
    enabled: model !== '',
  });

  const errors = useQuery({
    queryKey: ['admin', 'providers', provider, model, 'error-breakdown'],
    queryFn: () => fetchProviderErrorBreakdown(provider, model, '24h'),
    enabled: model !== '',
  });

  const recent = useQuery({
    queryKey: ['admin', 'providers', provider, model, 'recent-calls'],
    queryFn: () => fetchProviderRecentCalls(provider, model, 10),
    enabled: model !== '',
  });

  const p95Series = latency.data?.buckets.map((b) => b.p95Ms ?? 0) ?? [];
  const p50Series = latency.data?.buckets.map((b) => b.p50Ms ?? 0) ?? [];
  const callsSeries = latency.data?.buckets.map((b) => b.callCount) ?? [];

  return (
    <aside
      className="card detail-pane provider-drawer"
      data-testid="provider-drawer"
      style={{ position: 'fixed', right: 16, top: 84, bottom: 16, width: 460, overflow: 'auto', zIndex: 60 }}
    >
      <header className="card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3 className="card-title">
            {provider} · {model}
          </h3>
          <p className="page-sub mono">
            <span className={`kind-pill kind-${row.kind}`}>{row.kind}</span>
          </p>
        </div>
        <button type="button" className="btn" onClick={onClose} data-testid="provider-drawer-close">
          ✕
        </button>
      </header>

      <section className="pd-section">
        <h4 className="pd-section-title">Latency p50 / p95 (24h)</h4>
        {latency.isPending ? (
          <div className="loading">Cargando…</div>
        ) : latency.isError || latency.data?.buckets.length === 0 ? (
          <div className="empty">Sin datos</div>
        ) : (
          <>
            <div className="pd-kv">
              <span className="k">p50 buckets</span>
              <span className="v">
                <Sparkline values={p50Series} />
              </span>
              <span className="k">p95 buckets</span>
              <span className="v">
                <Sparkline values={p95Series} />
              </span>
              <span className="k">calls buckets</span>
              <span className="v">
                <Sparkline values={callsSeries} />
              </span>
            </div>
          </>
        )}
      </section>

      <section className="pd-section">
        <h4 className="pd-section-title">Error breakdown (24h)</h4>
        {errors.isPending ? (
          <div className="loading">Cargando…</div>
        ) : errors.isError || (errors.data?.errors.length ?? 0) === 0 ? (
          <div className="empty">Sin errores</div>
        ) : (
          <table className="data-table mini">
            <thead>
              <tr>
                <th>kind</th>
                <th>count</th>
                <th>last</th>
              </tr>
            </thead>
            <tbody>
              {errors.data.errors.map((e) => (
                <tr key={e.errorKind ?? 'null'}>
                  <td>{e.errorKind ?? '—'}</td>
                  <td className="mono">{formatNumber(e.count)}</td>
                  <td className="tt-tert">{formatDateTime(e.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="pd-section">
        <h4 className="pd-section-title">Last 10 calls</h4>
        {recent.isPending ? (
          <div className="loading">Cargando…</div>
        ) : recent.isError || (recent.data?.calls.length ?? 0) === 0 ? (
          <div className="empty">Sin llamadas</div>
        ) : (
          <table className="data-table mini">
            <thead>
              <tr>
                <th>span</th>
                <th>user</th>
                <th>dur</th>
                <th>cost</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recent.data.calls.map((c) => (
                <tr key={c.spanId}>
                  <td className="mono">{shortId(c.spanId, 12)}</td>
                  <td className="tt-tert">{c.userEmail ?? '—'}</td>
                  <td className="mono">{formatDurationMs(c.durationMs)}</td>
                  <td className="mono">
                    {(() => {
                      const cost = preferRawCost(c.providerCostNano, c.costNano);
                      return cost ? nanoToUsd(cost) : '—';
                    })()}
                  </td>
                  <td>{c.isError ? <span className="badge b-danger">err</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </aside>
  );
}
